import type { CardDef } from '../types.js';
import type { LadderEntry, LadderPage } from '../shared/tournament-types.js';
import type { MenuAction } from './MenuView.js';
import { apiClient } from '../api-client.js';
import { showCardDetail } from './CardDetailView.js';
import { mergeMissingCardsByIds } from '../collection/PlayerState.js';

// How many rows we pin at the top regardless of where the player is.
const TOP_SECTION_SIZE = 10;
// Half-window around the player when they're below the top section.
// Total initial window = 2*WINDOW_HALF + 1.
const WINDOW_HALF = 5;
// Page size when scroll-loading more rows beyond the initial window.
const SCROLL_PAGE = 25;

/** Full paginated ladder. Two visible sections:
 *
 *    1. **Top** — fixed `TOP_SECTION_SIZE` rows from rank 1 (always shown).
 *    2. **Window** — a band centered on the player's rank. Hidden if the
 *       player isn't in the tournament OR if they're in the top section
 *       (in which case the top section already shows them).
 *
 *  Two `IntersectionObserver` sentinels let the player expand the window
 *  by scrolling: scrolling toward the top divider loads more rows above
 *  the window (until it meets the top section), scrolling past the bottom
 *  edge loads more rows below the window. Jump buttons give a one-click
 *  way to bounce between the top and the player's row. */
export async function createLadderView(
  container: HTMLElement,
  tournamentId: string,
  tournamentName: string,
  catalog: Map<string, CardDef>,
  onAction: (action: MenuAction & { type: 'view-tournament' } | MenuAction) => void,
): Promise<() => void> {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen tournament-ladder-view is-scrollable';
  el.innerHTML = `
    <div class="tournament-sticky-header">
      <button class="menu-btn-sm tournament-sticky-back" id="back-btn">&larr; Back</button>
      <h1 class="tournament-sticky-title">${escapeHtml(tournamentName)}</h1>
      <div class="tournament-sticky-cta">
        <button class="menu-btn-sm" id="jump-top">↑ Top</button>
        <button class="menu-btn-sm" id="jump-me" style="display:none">⦿ Me</button>
      </div>
    </div>
    <div class="ladder-body">
      <div class="ladder-section ladder-top-section" id="ladder-top"></div>
      <div class="ladder-section-divider" id="ladder-divider" style="display:none">⋯</div>
      <div class="ladder-section-sentinel" id="ladder-sentinel-top"></div>
      <div class="ladder-section ladder-window-section" id="ladder-window"></div>
      <div class="ladder-section-sentinel" id="ladder-sentinel-bottom"></div>
      <div class="tournament-ladder-loading" id="ladder-loading" style="display:none">Loading…</div>
    </div>
  `;
  container.appendChild(el);

  el.querySelector('#back-btn')!.addEventListener('click', () => {
    onAction({ type: 'back-tournament-ladder', tournamentId });
  });

  const topEl = el.querySelector('#ladder-top') as HTMLElement;
  const dividerEl = el.querySelector('#ladder-divider') as HTMLElement;
  const windowEl = el.querySelector('#ladder-window') as HTMLElement;
  const sentinelTopEl = el.querySelector('#ladder-sentinel-top') as HTMLElement;
  const sentinelBottomEl = el.querySelector('#ladder-sentinel-bottom') as HTMLElement;
  const loadingEl = el.querySelector('#ladder-loading') as HTMLElement;
  const jumpMeBtn = el.querySelector('#jump-me') as HTMLElement;
  const jumpTopBtn = el.querySelector('#jump-top') as HTMLElement;

  // State: a list of contiguous range objects keeping the currently
  // loaded rows. We always have at most two: the top section and the
  // window. When the window grows up to meet the top section, we merge
  // them and hide the divider.
  let total = 0;
  let myRank: number | null = null;
  let topRange: { from: number; to: number } | null = null;     // 1-indexed inclusive
  let windowRange: { from: number; to: number } | null = null;
  let loading = false;
  let observer: IntersectionObserver | null = null;

  async function loadPage(offset: number, limit: number): Promise<LadderPage> {
    const page = await apiClient.getTournamentLadder(tournamentId, offset, limit);
    // Other players' profile cards may be customs the caller has never
    // owned; merge them into the catalog so the avatar lookup hits and
    // the card-detail modal works when clicked.
    await mergeMissingCardsByIds(catalog, page.entries.map(e => e.profileCardId));
    return page;
  }

  function renderSection(host: HTMLElement, entries: LadderEntry[]): void {
    host.innerHTML = '';
    const frag = document.createDocumentFragment();
    for (const e of entries) frag.appendChild(buildRow(e, catalog));
    host.appendChild(frag);
  }

  function updateDivider(): void {
    if (!topRange || !windowRange) {
      dividerEl.style.display = 'none';
      return;
    }
    // Divider shown only when there's a gap between top and window.
    dividerEl.style.display = windowRange.from > topRange.to + 1 ? '' : 'none';
  }

  function scrollToTop(): void {
    // `el` is the scrolling container (the .menu-screen.is-scrollable),
    // not the window — body / html have `overflow: hidden`. Scrolling
    // window does nothing here; we need to move the menu-screen's own
    // scrollTop.
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function scrollToMe(): void {
    if (myRank == null) return;
    const meRow = el.querySelector(`[data-rank="${myRank}"]`) as HTMLElement | null;
    if (!meRow) return;
    // `scrollIntoView({ block: 'center' })` would land the row behind
    // the sticky header (the header is ~60px tall and sits at the top
    // of the scroll container). Compute the offset manually so the
    // row sits below the header with a bit of breathing room.
    const header = el.querySelector('.tournament-sticky-header') as HTMLElement | null;
    const headerH = header?.getBoundingClientRect().height ?? 0;
    const target = meRow.offsetTop - headerH - 24;
    el.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }

  async function init(): Promise<void> {
    loadingEl.style.display = '';
    try {
      // First fetch: top page. We piggyback the total off this response
      // and use it to clamp the window.
      const topPage = await loadPage(0, TOP_SECTION_SIZE);
      total = topPage.total;
      renderSection(topEl, topPage.entries);
      topRange = { from: 1, to: topPage.entries.length };

      // Locate "me" in the top page if present, else figure out my rank
      // by fetching a small probe around the bottom.
      const meInTop = topPage.entries.find(e => e.isMe);
      if (meInTop) {
        myRank = meInTop.rank;
      } else {
        // The detail endpoint already knows the rank, but we don't have
        // it here. Probe by paging deeper until we find isMe — but only
        // if total is reasonable. For very large tournaments, the
        // "near-me" window from /api/tournaments/:id detail gives this
        // info; here we approximate by scanning. Cap so we don't make
        // dozens of HTTP calls.
        myRank = await locateMe(topPage.total);
      }

      // If "me" exists and isn't in the top section, render the window
      // centered on me.
      if (myRank != null && myRank > TOP_SECTION_SIZE) {
        const from = Math.max(TOP_SECTION_SIZE + 1, myRank - WINDOW_HALF);
        const to = Math.min(total, myRank + WINDOW_HALF);
        const winPage = await loadPage(from - 1, to - from + 1);
        renderSection(windowEl, winPage.entries);
        windowRange = { from, to };
        jumpMeBtn.style.display = '';
      } else {
        // Either not joined, or in the top section. No window section
        // shown; scrolling at the bottom still loads more.
        windowEl.style.display = '';
        windowRange = null;
        jumpMeBtn.style.display = myRank != null ? '' : 'none';
      }

      updateDivider();
      attachObservers();
    } catch (err) {
      console.error('Failed to load ladder', err);
    } finally {
      loadingEl.style.display = 'none';
    }
  }

  /** Find the calling user's rank by paging deeper from the top. Capped
   *  by `MAX_LOCATE_PAGES` so we never make more than that many calls.
   *  The detail endpoint is the right source long-term — this is a
   *  fallback for when the caller didn't pass myRank in. */
  async function locateMe(totalRows: number): Promise<number | null> {
    const MAX_LOCATE_PAGES = 5;
    const PAGE = 50;
    for (let i = 0; i < MAX_LOCATE_PAGES; i++) {
      const offset = TOP_SECTION_SIZE + i * PAGE;
      if (offset >= totalRows) break;
      const page = await loadPage(offset, PAGE);
      const me = page.entries.find(e => e.isMe);
      if (me) return me.rank;
    }
    return null;
  }

  /** Expand the window section upward (toward the top section). Stops
   *  when the window's `from` reaches `topRange.to + 1`. If the two
   *  sections become adjacent, they're merged into the top section and
   *  the window section is hidden. */
  async function expandUp(): Promise<void> {
    if (!windowRange || !topRange || loading) return;
    const newFrom = Math.max(topRange.to + 1, windowRange.from - SCROLL_PAGE);
    if (newFrom >= windowRange.from) return;
    loading = true;
    loadingEl.style.display = '';
    try {
      const added = await loadPage(newFrom - 1, windowRange.from - newFrom);
      const frag = document.createDocumentFragment();
      for (const e of added.entries) frag.appendChild(buildRow(e, catalog));
      windowEl.prepend(frag);
      windowRange = { from: newFrom, to: windowRange.to };
      // Merge sections if adjacent.
      if (windowRange.from === topRange.to + 1) {
        // Move all window rows into the top section, hide divider.
        while (windowEl.firstChild) topEl.appendChild(windowEl.firstChild);
        topRange = { from: 1, to: windowRange.to };
        windowRange = null;
      }
      updateDivider();
    } finally {
      loading = false;
      loadingEl.style.display = 'none';
    }
  }

  /** Expand the window (or top section if no window) downward. */
  async function expandDown(): Promise<void> {
    if (loading) return;
    const active = windowRange ?? topRange;
    if (!active) return;
    if (active.to >= total) return;
    const newTo = Math.min(total, active.to + SCROLL_PAGE);
    loading = true;
    loadingEl.style.display = '';
    try {
      const added = await loadPage(active.to, newTo - active.to);
      const frag = document.createDocumentFragment();
      for (const e of added.entries) frag.appendChild(buildRow(e, catalog));
      const host = windowRange ? windowEl : topEl;
      host.appendChild(frag);
      if (windowRange) windowRange = { from: windowRange.from, to: newTo };
      else if (topRange) topRange = { from: 1, to: newTo };
      updateDivider();
    } finally {
      loading = false;
      loadingEl.style.display = 'none';
    }
  }

  function attachObservers(): void {
    // The scrolling container is `el` (the .menu-screen.is-scrollable),
    // not the viewport — body/html have overflow:hidden. The observer
    // needs `root: el` so intersections fire when the sentinels enter
    // the menu-screen's visible area as the user scrolls *inside* the
    // container.
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (entry.target === sentinelTopEl) void expandUp();
        if (entry.target === sentinelBottomEl) void expandDown();
      }
    }, { root: el, rootMargin: '120px' });
    observer.observe(sentinelTopEl);
    observer.observe(sentinelBottomEl);
  }

  jumpTopBtn.addEventListener('click', scrollToTop);
  jumpMeBtn.addEventListener('click', scrollToMe);

  await init();

  return () => observer?.disconnect();
}

function buildRow(e: LadderEntry, catalog: Map<string, CardDef>): HTMLElement {
  const row = document.createElement('div');
  row.className = `tournament-ladder-row ${e.isMe ? 'is-me' : ''} ${e.isBot ? 'is-bot' : ''}`;
  row.setAttribute('data-rank', String(e.rank));
  row.innerHTML = `
    <span class="tournament-ladder-rank">#${e.rank}</span>
    ${renderAvatarHtml(e, catalog)}
    <span class="tournament-ladder-name">${escapeHtml(e.displayName)}${e.isBot ? ' <span class="tournament-ladder-bot-tag">bot</span>' : ''}</span>
    <span class="tournament-ladder-record">${e.wins}-${e.losses}</span>
  `;
  // Avatar click → card detail. Stop propagation so the row's own
  // click (if we ever add one) doesn't fire as well.
  const avatar = row.querySelector('.tournament-ladder-avatar[data-card]') as HTMLElement | null;
  if (avatar) {
    const cardId = avatar.dataset.card;
    avatar.addEventListener('click', (ev) => {
      ev.stopPropagation();
      if (cardId) showCardDetail(cardId, catalog);
    });
  }
  return row;
}

/** Avatar cell for a ladder row. Returns either an `<img>` wrapped
 *  in a clickable host span (for entries with a profile card whose
 *  art is in the catalog) or a non-clickable monogram placeholder
 *  (for missing/unset profile cards). Mirrors the helper in
 *  TournamentDetailView so the two ladder surfaces look identical. */
function renderAvatarHtml(e: LadderEntry, catalog: Map<string, CardDef>): string {
  const def = e.profileCardId ? catalog.get(e.profileCardId) : null;
  if (!def || !def.art) {
    const monogram = e.displayName.split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 2).toUpperCase();
    return `<span class="tournament-ladder-avatar tournament-ladder-avatar-empty">${escapeHtml(monogram)}</span>`;
  }
  return `<span class="tournament-ladder-avatar" data-card="${escapeHtml(e.profileCardId!)}" role="button" tabindex="0" title="View card">
    <img src="${escapeHtml(def.art)}" alt="${escapeHtml(def.name)}" draggable="false" loading="lazy" />
  </span>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
